/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useRef, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { CallbackRef, useForkRef } from "../../hooks/useForkRef";
import { IDraggableInfo, TDroppableID } from "./DragAndDropTypes";
import { useDragAndDropContext } from "./DragAndDropContext";
import { DragAndDropPlaceholder } from "./DragAndDropPlaceholder";
import * as attributes from "./dragAndDropAttributes";
import { DroppableContext, IDroppableContext } from "./DroppableContext";
import { useDeepCompareMemo } from "use-deep-compare";

export interface IDroppableProps extends React.ComponentPropsWithRef<"div"> {
	className?: string;
	droppableId: TDroppableID;
	children:
		| React.ReactNode
		| ((props: {
				droppableRef: CallbackRef;
				droppableProps: Record<string, string>;
				placeholder: React.ReactNode;
		  }) => JSX.Element);
}

export const Droppable = React.forwardRef<HTMLDivElement, IDroppableProps>(
	({ children, className, droppableId, ...props }, ref) => {
		const baseClass = BEM("edt-dnd-droppable");

		const droppableRef = useRef<HTMLDivElement>();
		const forkRef = useForkRef(droppableRef, ref);

		const { registry, contextId } = useDragAndDropContext();

		const [ dragging, setDragging ] = useState<IDraggableInfo>(null);

		const droppableProps = {
			[attributes.droppable.droppableId]: droppableId,
			[attributes.droppable.contextId]: contextId
		};

		if (className) {
			baseClass.add(className);
		}

		const placeholder = useDeepCompareMemo(
			() =>
				dragging ? <DragAndDropPlaceholder contextId={contextId} draggableInfo={dragging} /> : null,
			[ dragging, contextId ]
		);

		const droppableContextValue = useDeepCompareMemo<IDroppableContext>(
			() => ({
				droppableId,
				draggignOver: !!dragging
			}),
			[ droppableId, dragging ]
		);

		useEffect(() => {
			registry.droppable.register({
				id: droppableId,
				onDragMove: ({ dragging, draggingOver }) => {
					setDragging(() => (draggingOver === droppableId ? dragging : null));
				},
				onDragDrop: () => {
					setDragging(null);
				}
			});

			return () => registry.droppable.unregister(droppableId);
		}, [ droppableId ]);

		return (
			<DroppableContext.Provider value={droppableContextValue}>
				{isFunction(children) ? (
					children({ droppableRef: forkRef, droppableProps, placeholder })
				) : (
					<div className={baseClass.toClassName()} ref={forkRef} {...props} {...droppableProps}>
						{children}
						{placeholder}
					</div>
				)}
			</DroppableContext.Provider>
		);
	}
);
